import { Action } from "redux";

import { IMenuNavigationConfig, NodeDefinitions } from "@multiweb/types/multiweb";
import { ConstrainedDeepRequired, SerializableKeyEvent, TypedObject } from "@multiweb/types/react";
import {
    ACTION_ITEM_FOCUSED,
    CLEAR_FOCUSED_ITEM,
    CLEAR_NAVIGATION_SECTION,
    FOCUS_DEFAULT_ITEM,
    KEY_PRESSED,
    MENU_FOCUS_LOST,
    NAVIGATION_NODES_UPDATED,
    SET_FOCUSED_ITEM,
    SET_NAVIGATION_NODES,
} from "./constants";

/**
 * Obsahuje staticky definované sekce pro navigaci.
 * @see NavigationSection
 */
export enum MenuNavigationSectionType {
    /** Statické prvky navigace, jako levý panel menu nebo menu uživatele. */
    static = "static",
    forms = "forms",
    /** Položky první úrovně formulářů, které se zobrazují v panelu menu. */
    panelForms = "panelForms",
    quickAccess = "quickAccess",
    quickAccessOpened = "quickAccessOpened",
    quickAccessHistoric = "quickAccessHistoric",
    quickAccessFavorite = "quickAccessFavorite",
    searchResults = "searchResults",
    searchResultsOpened = "searchResultsOpened",
    searchResultsHistoric = "searchResultsHistoric",
    searchResultsFavorite = "searchResultsFavorite",
    notificationGroups = "notificationGroups",
    notifications = "notifications",
}

/**
 * Typy zaměřitelných položek v menu.
 */
export enum MenuFocusableItemType {
    /** Typ libovolné položky v menu panelu, zahrnuje statické položky, kořenové položky formulářů a jejich složek, notifikační skupiny. */
    panelItem = "panelItem",
    searchInput = "searchInput",
    showAllNotificationsButton = "showAllNotificationsButton",
    setGroupNotificationsAsReadButton = "setGroupNotificationsAsReadButton",
    formItem = "formItem",
    folderItem = "folderItem",
    profile = "profile",
    openedItem = "openedItem",
    notificationItem = "notificationItem",
    userMenuItem = "userMenuItem",
    favoriteActionItem = "favoriteActionItem",
    openActionItem = "openActionItem",
    openInTabActionItem = "openInTabActionItem",
    closeActionItem = "closeActionItem",
}

/**
 * Typy zaměřitelné položky. Rozšiřuje základní typ {@link MenuFocusableItemType} o obecný řetězec. To umožňuje vytváření
 * vlastních typů položek při customizaci menu a navigace.
 */
export type FocusableItemType = MenuFocusableItemType | string;

/**
 * Typy zaměřitelných akčních položek používaných v kontextových menu.
 */
export const FocusableActionItemTypes: readonly FocusableItemType[] = [
    MenuFocusableItemType.favoriteActionItem,
    MenuFocusableItemType.openActionItem,
    MenuFocusableItemType.openInTabActionItem,
    MenuFocusableItemType.closeActionItem,
];

/**
 * Sekce z hlediska navigace klávesnicí. Rozdělení umožňuje rozlišit stejné prvky s jinou topologií. Např. položka formuláře může být přítomna
 * zároveň v sekci formulářů, tak v sekci oblíbených formulářů, bude však mít odlišné sousedy. Takové položky se z hlediska navigace rozliší pomocí sekce, do které patří.
 * Může to být staticky definovaná sekce nebo dynamická sekce. Dynamické sekce jsou nutné pro případy, kdy se sekce vytvářejí dynamicky na základě dat a zároveň tyto
 * sekce mohou obsahovat stejnou položku.
 *
 * Tento případ nastává například při vyhledávání v sekci Rychlý přístup, kde se seskupují záznamy podle kořenového formuláře. Kořenové formuláře tak vytváří dynamické
 * sekce. Zároveň může nastat případ, kdy je jeden profil odkazovaný z položek patřících k různým kořenovým formulářům.
 *
 * Rozdělení na sekce také umožňuje efektivnější správu stavu při aktualizaci navigační sekce.
 */
export type NavigationSection = MenuNavigationSectionType | string;

/**
 * Reprezentuje zaměřitelnou položku v menu pro účel navigace pomocí klávesnice.
 */
export type FocusableItem = {
    /** Id unikátní v rámci všech uzlů menu. Složeno z itemId, type a section. */
    readonly id: FocusableItemId;
    /** Id unikátní v rámci typu. Např. pro typ profilu se jedná o id profilu. */
    readonly itemId: number | string;
    /** Typ zaměřitelné položky. */
    readonly type: FocusableItemType;
    /** Navigační sekce, do které zaměřitelná položka patří. */
    readonly section: NavigationSection;
};

/**
 * Typ parametru pro selector isItemFocused.
 */
export type IsItemFocusedSelectorParams = Pick<FocusableItem, "itemId" | "type" | "section">;

/**
 * Uzel navigace, který není ve stavu a je možné u něj upravovat odkazy na sousední uzly.
 */
export type MutableNavigationNode = {
    /** Zaměřitelná položka, kterou reprezentuje tento uzel. */
    readonly self: FocusableItem;
    /** Id zaměřitelné položky nacházející se nad touto položkou. */
    upId?: FocusableItemId;
    /** Id zaměřitelné položky nacházející se pod touto položkou. */
    downId?: FocusableItemId;
    /** Id zaměřitelné položky nacházející se vpravo od této položky. */
    rightId?: FocusableItemId;
    /** Id zaměřitelné položky nacházející se vlevo od této položky. */
    leftId?: FocusableItemId;
    /** Id zaměřitelné položky na kterou se přechází při zrušení zaměření této položky. */
    escId?: FocusableItemId;
};

/**
 * Reprezentuje uzel v topologii navigace pomocí kláves v menu.
 */
export type NavigationNode = Readonly<MutableNavigationNode>;

/**
 * Obsahuje navigační uzly klíčované podle id.
 */
export type NavigationSectionNodes = TypedObject<NavigationNode>;

/**
 * Reprezentuje topologii pro navigaci pomocí klávesnice v menu.
 */
export type NavigationNodes = TypedObject<NavigationSectionNodes>;

/**
 * Id zaměřitelné položky. Složené z typu položky a id položky.
 */
export type FocusableItemId = string;

/**
 * Reprezentuje část stavu menu uchovávající data o navigaci pomocí klávesnice.
 */
export interface IMenuNavigationState {
    /** Id aktuálně zaměřené položky. */
    readonly focusedItemId: FocusableItemId | null;
    /** Id první zaměřitelné položky ze sekce Rychlý přístup. Slouží k rychlému přepojení uzlů při zrušení vyhledávání. */
    readonly firstQuickAccessFocusableItemId: FocusableItemId | null;
    /** Id poslední zaměřitelné položky ze sekce Rychlý přístup. Slouží k rychlému přepojení uzlů při zrušení vyhledávání. */
    readonly lastQuickAccessFocusableItemId: FocusableItemId | null;
    /**
     * Indikátor, zda se má při zaměření vyhledávacího pole označit celý text.
     * Řeší případ, kdy se po napsání prvního písmene vymění komponenty a tím se nechtěně označí text.
     */
    readonly searchInputSelectAllText: boolean;
    /** Navigační uzly všech zaměřitelných položek v menu. */
    readonly navigationNodes: NavigationNodes;
}

/**
 * Typ pro nastavení navigace obsahující všechna nastavení.
 */
export type CompleteNavigationConfig = ConstrainedDeepRequired<IMenuNavigationConfig, NodeDefinitions>;

export interface ISetFocusedItemAction extends Action<typeof SET_FOCUSED_ITEM> {
    readonly payload: {
        readonly focusedItemId: FocusableItemId;
    };
}

export class ISetFocusedItemAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SET_FOCUSED_ITEM;
    }
}

export interface ISetNavigationNodesAction extends Action<typeof SET_NAVIGATION_NODES> {
    readonly payload: {
        readonly section: NavigationSection;
        readonly navigationNodes: NavigationNode[];
    };
}

export class ISetNavigationNodesAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SET_NAVIGATION_NODES;
    }
}

export interface IClearFocusedItemAction extends Action<typeof CLEAR_FOCUSED_ITEM> {}

export class IClearFocusedItemAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === CLEAR_FOCUSED_ITEM;
    }
}

export interface IFocusDefaultItemAction extends Action<typeof FOCUS_DEFAULT_ITEM> {}

export class IFocusDefaultItemAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === FOCUS_DEFAULT_ITEM;
    }
}

export interface IClearNavigationSectionAction extends Action<typeof CLEAR_NAVIGATION_SECTION> {
    readonly payload: {
        readonly section: NavigationSection;
    };
}

export class IClearNavigationSectionAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === CLEAR_NAVIGATION_SECTION;
    }
}

export interface INavigationNodesUpdatedAction extends Action<typeof NAVIGATION_NODES_UPDATED> {
    readonly payload: {
        readonly sectionType: NavigationSection;
    };
}

export class INavigationNodesUpdatedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === NAVIGATION_NODES_UPDATED;
    }
}

export interface IKeyPressedAction extends Action<typeof KEY_PRESSED> {
    readonly payload: {
        event: SerializableKeyEvent;
    };
}

export class IKeyPressedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === KEY_PRESSED;
    }
}

export interface IMenuFocusLostAction extends Action<typeof MENU_FOCUS_LOST> {
    readonly payload: {
        readonly documentHidden: boolean;
    };
}

export class IMenuFocusLostAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === MENU_FOCUS_LOST;
    }
}

export interface IActionItemFocusedAction extends Action<typeof ACTION_ITEM_FOCUSED> {
    readonly payload: {
        readonly focusableItemId: FocusableItemId;
    };
}

export class IActionItemFocusedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === ACTION_ITEM_FOCUSED;
    }
}
